/**
 * Function Notation generator.
 *
 * Define a linear function f(x) = m x + b and ask the student to evaluate it
 * (find f(k)) or, at higher difficulty, find the input for a given output.
 * The function is a LinearExpr, so the expected value is computed by exact
 * evaluation - the same routine used to verify it independently.
 *
 * Families:
 *   N1: evaluate f(k)                       -> number
 *   N2: evaluate with a negative input      -> number
 *   N3: find x given f(x) = target          -> number (inverse)
 *   N4: two-step rule f(x) = m(x + c)        -> number
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr, LinearEquation } from "../engine/linear";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "function-notation" as const;
const VERSION = "1.0.0";

type Family = "N1" | "N2" | "N3" | "N4";

interface Cfg {
  readonly families: readonly Family[];
  readonly mMax: number;
  readonly bMax: number;
  readonly inMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["N1"], mMax: 5, bMax: 9, inMax: 8, pNeg: 0.1 },
  standard: { families: ["N1", "N2", "N3"], mMax: 7, bMax: 12, inMax: 10, pNeg: 0.4 },
  hard: { families: ["N2", "N3", "N4"], mMax: 9, bMax: 12, inMax: 12, pNeg: 0.5 },
  challenge: { families: ["N3", "N4"], mMax: 11, bMax: 12, inMax: 12, pNeg: 0.55 },
};

function ruleLatex(m: Rational, b: Rational): string {
  const mPart = m.equals(rat(1)) ? "x" : m.equals(rat(-1)) ? "-x" : `${m.toLatex()}x`;
  if (b.isZero()) return `f(x) = ${mPart}`;
  return `f(x) = ${mPart} ${b.isNegative() ? `- ${b.neg().toLatex()}` : `+ ${b.toLatex()}`}`;
}

/** Wrap a value in parentheses when it is negative, e.g. "(-3)"; else bare. */
function paren(v: Rational): string {
  return v.isNegative() ? `(${v.toLatex()})` : v.toLatex();
}

/** A signed continuation term for a sum, e.g. "+ 5" or "- 5". */
function signedTerm(v: Rational): string {
  return v.isNegative() ? `- ${v.neg().toLatex()}` : `+ ${v.toLatex()}`;
}

/** The "m x" part with sensible 1/-1 handling. */
function mVarLatex(m: Rational): string {
  return m.equals(rat(1)) ? "x" : m.equals(rat(-1)) ? "-x" : `${m.toLatex()}x`;
}

export class FunctionNotationGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const m = rat(rng.int(2, cfg.mMax) * rng.sign(cfg.pNeg));
      const b = rat(rng.int(1, cfg.bMax) * rng.sign(cfg.pNeg));

      let promptText: string;
      let promptLatex: string;
      let answer: Rational;
      let steps: { description: string; latex: string }[];
      const rule = family === "N4" ? undefined : new LinearExpr(m, b);

      if (family === "N3") {
        // find x given f(x) = target, target chosen so x is an integer.
        const x = rat(rng.intNonZero(-cfg.inMax, cfg.inMax));
        const target = (rule as LinearExpr).evaluate(x);
        answer = x;
        promptText = `${ruleLatex(m, b)}. Find x when f(x) = ${target.toString()}.`;
        promptLatex = `${ruleLatex(m, b)}\\text{. Find } x \\text{ when } f(x) = ${target.toLatex()}.`;
        // Verify independently by solving m x + b = target.
        const eq = new LinearEquation(rule as LinearExpr, LinearExpr.constantExpr(target));
        if (!eq.solve().value?.equals(x)) continue;
        // Show: set rule = target, subtract b, divide by m.
        const afterSub = target.sub(b);
        steps = [
          {
            description: "This time we know the output. Set the rule equal to it.",
            latex: `${mVarLatex(m)} ${signedTerm(b)} = ${target.toLatex()}`,
          },
        ];
        if (!b.isZero()) {
          steps.push({
            description: `Undo the ${b.isNegative() ? "subtraction" : "addition"} of ${b.abs().toLatex()}: ${b.isNegative() ? "add" : "subtract"} it on both sides.`,
            latex: `${mVarLatex(m)} = ${afterSub.toLatex()}`,
          });
        }
        steps.push({
          description: `Undo the multiplication by ${m.toLatex()}: divide both sides by ${m.toLatex()}.`,
          latex: `x = \\frac{${afterSub.toLatex()}}{${m.toLatex()}} = ${x.toLatex()}`,
        });
      } else if (family === "N4") {
        // f(x) = m(x + c); evaluate at k.
        const c = rat(rng.int(1, cfg.bMax) * rng.sign(cfg.pNeg));
        const k = rat(rng.intNonZero(-cfg.inMax, cfg.inMax));
        const f = LinearExpr.variable().add(LinearExpr.constantExpr(c)).scale(m);
        answer = f.evaluate(k);
        const inner = k.add(c); // value inside the parentheses after substituting
        const cPart = c.isNegative() ? `- ${c.neg().toLatex()}` : `+ ${c.toLatex()}`;
        promptText = `f(x) = ${m.toString()}(x ${c.isNegative() ? "- " + c.neg().toString() : "+ " + c.toString()}). Find f(${k.toString()}).`;
        promptLatex = `f(x) = ${m.toLatex()}(x ${cPart})\\text{. Find } f(${k.toLatex()}).`;
        steps = [
          {
            description: `Substitute ${k.toLatex()} for x. Because the input is used more than once, keep it in parentheses.`,
            latex: `f(${k.toLatex()}) = ${m.toLatex()}(${paren(k)} ${cPart})`,
          },
          {
            description: "Simplify inside the parentheses first (order of operations).",
            latex: `= ${m.toLatex()}(${inner.toLatex()})`,
          },
          {
            description: "Then multiply.",
            latex: `= ${answer.toLatex()}`,
          },
        ];
      } else {
        // N1/N2: evaluate f(k).
        const k = rat(family === "N2" ? -rng.int(1, cfg.inMax) : rng.intNonZero(-cfg.inMax, cfg.inMax));
        answer = (rule as LinearExpr).evaluate(k);
        const product = m.mul(k);
        promptText = `${ruleLatex(m, b)}. Find f(${k.toString()}).`;
        promptLatex = `${ruleLatex(m, b)}\\text{. Find } f(${k.toLatex()}).`;
        steps = [
          {
            description: `Substitute ${k.toLatex()} for x wherever it appears.`,
            latex: `f(${k.toLatex()}) = ${m.toLatex()}(${paren(k)})${b.isZero() ? "" : " " + signedTerm(b)}`,
          },
        ];
        if (b.isZero()) {
          steps.push({ description: "Multiply.", latex: `= ${answer.toLatex()}` });
        } else {
          steps.push({
            description: "Multiply first (order of operations).",
            latex: `= ${product.toLatex()} ${signedTerm(b)}`,
          });
          steps.push({
            description: `Then ${b.isNegative() ? "subtract" : "add"}.`,
            latex: `= ${answer.toLatex()}`,
          });
        }
      }

      // Quality: avoid a zero answer (reads as trivial for this topic).
      if (answer.isZero()) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "f(k) means substitute k wherever you see x." },
          { level: 2, text: "Follow order of operations after substituting." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const functionNotation = new FunctionNotationGenerator();
